import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../api/apiClient';
import { statusPill, formatTs, pre, monoShort } from './AuditTable';
import { SampleTable } from './tabs/CypherExecutionsTab';

function parseSample(raw) {
  if (!raw) return null;
  try { return typeof raw === 'string' ? JSON.parse(raw) : raw; }
  catch (_) { return null; }
}

export default function TraceDetailDrawer({ traceId, onClose }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verify, setVerify] = useState(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (!traceId) return;
    let cancelled = false;
    setLoading(true);
    apiService.getAuditTrace(traceId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ events: [], trace: { id: traceId } }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [traceId]);

  const runVerify = useCallback(async () => {
    setVerifying(true);
    try {
      const r = await apiService.verifyAuditTrace(traceId);
      setVerify(r);
    } catch (e) {
      setVerify({ valid: false, reason: 'request_failed' });
    } finally {
      setVerifying(false);
    }
  }, [traceId]);

  const exportTrace = useCallback(async () => {
    try {
      const r = await apiService.exportAuditTrace(traceId);
      const blob = new Blob([JSON.stringify(r, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trace-${traceId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) {}
  }, [traceId]);

  if (!traceId) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <aside className="relative w-full max-w-2xl h-full bg-surface border-l border-border-subtle overflow-auto">
        <header className="sticky top-0 bg-surface border-b border-border-subtle px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-label tracking-widest text-ink-muted uppercase">Trace</div>
            <code className="text-sm text-ink">{traceId}</code>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={runVerify}
              disabled={verifying}
              className="text-label tracking-widest px-3 py-1.5 border border-border-strong rounded hover:text-accent-text disabled:opacity-50"
            >
              {verifying ? t('traceability:integrity.verifying') : t('traceability:actions.verify')}
            </button>
            <button
              type="button"
              onClick={exportTrace}
              className="text-label tracking-widest px-3 py-1.5 border border-border-strong rounded hover:text-accent-text"
            >
              {t('traceability:actions.export')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-ink-secondary hover:text-ink px-2"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </header>

        {verify && (
          <div className={`mx-6 mt-4 px-4 py-2 text-sm rounded border ${
            verify.valid
              ? 'bg-emerald-50 text-emerald-800 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-200 dark:border-emerald-900'
              : 'bg-red-50 text-red-800 border-red-300 dark:bg-red-950/60 dark:text-red-200 dark:border-red-800'
          }`}>
            {verify.valid
              ? t('traceability:integrity.verified', { count: verify.events_checked || 0 })
              : t('traceability:integrity.broken', { id: verify.break_at, reason: verify.reason || '?' })}
          </div>
        )}

        <div className="p-6">
          {loading && <div className="text-ink-muted">···</div>}
          {!loading && data && (
            <>
              <section className="mb-6">
                <div className="text-label tracking-widest text-ink-muted uppercase mb-2">Metadata</div>
                <dl className="grid grid-cols-2 gap-y-1 text-sm">
                  <dt className="text-ink-muted">kind</dt>
                  <dd className="text-ink-secondary">{data.trace?.kind || '—'}</dd>
                  <dt className="text-ink-muted">actor</dt>
                  <dd className="text-ink-secondary">{data.trace?.actor || '—'}</dd>
                  <dt className="text-ink-muted">status</dt>
                  <dd className="text-ink-secondary">{statusPill(data.trace?.status)}</dd>
                  <dt className="text-ink-muted">started</dt>
                  <dd className="text-ink-secondary">{formatTs(data.trace?.started_at)}</dd>
                  <dt className="text-ink-muted">ended</dt>
                  <dd className="text-ink-secondary">{formatTs(data.trace?.ended_at)}</dd>
                  <dt className="text-ink-muted">latency</dt>
                  <dd className="text-ink-secondary">{data.trace?.latency_ms ?? '—'} ms</dd>
                </dl>
              </section>

              <section>
                <div className="text-label tracking-widest text-ink-muted uppercase mb-3">Timeline ({data.events?.length || 0})</div>
                <ol className="space-y-3">
                  {(data.events || []).map((ev) => (
                    <li key={ev.event_id} className="border-l-2 border-border-subtle pl-4">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-label tracking-widest text-ink-muted">{formatTs(ev.timestamp)}</span>
                        <span className="text-xs text-accent-text font-medium">{ev.event_type}</span>
                        {statusPill(ev.status)}
                        {ev.latency_ms != null && (
                          <span className="text-xs text-ink-muted">{ev.latency_ms} ms</span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-1 text-xs text-ink-secondary">
                        {ev.phase && <div><span className="text-ink-muted">phase:</span> {ev.phase}</div>}
                        {ev.model && <div><span className="text-ink-muted">model:</span> {ev.model}</div>}
                        {(ev.tokens_in != null || ev.tokens_out != null) && (
                          <div>
                            <span className="text-ink-muted">tokens:</span>{' '}
                            in={ev.tokens_in ?? 0} · out={ev.tokens_out ?? 0}
                            {ev.thoughts_tokens ? <> · thoughts={ev.thoughts_tokens}</> : null}
                          </div>
                        )}
                        {ev.finish_reason && <div><span className="text-ink-muted">finish_reason:</span> {ev.finish_reason}</div>}
                        {ev.rows_returned != null && <div><span className="text-ink-muted">rows:</span> {ev.rows_returned}</div>}
                        {ev.cypher && <div className="mt-1">{pre(ev.cypher, 400)}</div>}
                        {ev.rows_sample_json && (() => {
                          const sample = parseSample(ev.rows_sample_json);
                          if (!Array.isArray(sample) || sample.length === 0) return null;
                          return (
                            <details className="mt-1">
                              <summary className="cursor-pointer text-ink-muted">
                                muestra ({sample.length} filas)
                              </summary>
                              <SampleTable rows={sample} />
                            </details>
                          );
                        })()}
                        {ev.prompt_preview && <div className="mt-1"><span className="text-ink-muted">prompt:</span>{pre(ev.prompt_preview, 300)}</div>}
                        {ev.thoughts_preview && (
                          <details className="mt-1 border-l-2 border-accent/40 pl-2">
                            <summary className="cursor-pointer text-accent-text">💭 razonamiento del modelo ({ev.thoughts_tokens ?? '?'} tokens)</summary>
                            {pre(ev.thoughts_preview, 1200)}
                          </details>
                        )}
                        {ev.response_preview && <div><span className="text-ink-muted">response:</span>{pre(ev.response_preview, 300)}</div>}
                        {ev.error_message && <div className="text-red-300">err: {ev.error_message}</div>}
                      </div>
                      <div className="mt-1 text-label tracking-widest text-ink-muted">
                        chain: {monoShort(ev.chain_hash, 12)}
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
